import { memo, useCallback, useEffect, useState } from 'react'
import { Col, Row } from 'react-grid-system'
import { useUserLanguage } from '@/customHooks'
import { CreateIncidentReportInput } from '@/graphql/generated'
import { ImageUploader, NormalFormTextArea, NormalFormTextField, VideoUploader } from '../molecules'
import textsData from '@/textData/AddIncidentReportFormTexts.json'

export type AddIncidentReportFormStateProps = Omit<CreateIncidentReportInput, 'fileUploadsIds'>

type AddIncidentReportFormProps = {
  defaultReportData: AddIncidentReportFormStateProps | null
  onChange: (reportData: AddIncidentReportFormStateProps, pictures: File[], videos: File[]) => void
}

const emptyData: AddIncidentReportFormStateProps = {
  subject: '',
  note: {
    description: ''
  }
}

export const AddIncidentReportFormControlled = memo(({ defaultReportData, onChange }: AddIncidentReportFormProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.form : textsData.en.form

  const [reportData, setReportData] = useState<AddIncidentReportFormStateProps>(defaultReportData || emptyData)
  const [pictures, setPictures] = useState<File[]>([])
  const [videos, setVideos] = useState<File[]>([])

  useEffect(() => {
    setReportData(defaultReportData || emptyData)
  }, [defaultReportData])

  const onFieldChangeHandle = useCallback((field: keyof AddIncidentReportFormStateProps, value: string | null) => {
    const newValue = value || ''

    let updatedReportData: AddIncidentReportFormStateProps;

    if (field === 'note') updatedReportData = { ...reportData, [field]: { description: newValue } }
    else updatedReportData = { ...reportData, [field]: newValue }

    setReportData(updatedReportData)
    onChange(updatedReportData, pictures, videos)
  }, [onChange, reportData, pictures, videos])

  const onPicturesReadyHandle = useCallback((newPictures: File[]) => {
    setPictures(newPictures)
    onChange(reportData, newPictures, videos)
  }, [onChange, reportData, videos])

  const onVideosReadyHandle = useCallback((newVideos: File[]) => {
    setVideos(newVideos)
    onChange(reportData, pictures, newVideos)
  }, [onChange, reportData, pictures])

  return (
    <>
      <Row>
        <Col xs={12}>
          <NormalFormTextField
            name="subject"
            label={texts.subject.label}
            value={reportData.subject}
            placeholder={texts.subject.placeholder}
            onChange={(value) => onFieldChangeHandle('subject', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <NormalFormTextArea
            name="note"
            label={texts.note.label}
            value={reportData.note.description}
            placeholder={texts.note.placeholder}
            onChange={(value) => onFieldChangeHandle('note', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <ImageUploader onUploadReady={onPicturesReadyHandle} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <VideoUploader onUploadReady={onVideosReadyHandle} />
        </Col>
      </Row>
    </>
  )
})
